import { ref, computed, watch } from 'vue';
import { useDisplay } from 'vuetify';

interface PaginationOptions<T> {
  // The store that contains the pagination data
  store: T;
  // Function to get the total pages from the store
  getTotalPages: (store: T) => number;
  // Function to get the current page from the store
  getCurrentPage: (store: T) => number;
  // Function to set the page in the store
  setPage: (store: T, page: number) => void;
  // Optional function to fetch data when the page changes
  fetchData?: (store: T) => Promise<void>;
  // Optional custom values for totalVisible on desktop and mobile
  desktopVisibleCount?: number;
  mobileVisibleCount?: number;
}

export function usePagination<T>(options: PaginationOptions<T>) {
  const {
    store,
    getTotalPages,
    getCurrentPage,
    setPage,
    fetchData,
    desktopVisibleCount = 7,
    mobileVisibleCount = 4
  } = options;

  // Create a local ref for the page number
  const pageNumber = ref(getCurrentPage(store));
  
  // Compute the total pages from the store
  const pageLength = computed(() => getTotalPages(store));
  
  // Use responsive design to determine how many pagination buttons to show
  const { mdAndUp } = useDisplay();
  const totalVisible = computed(() => 
    mdAndUp.value ? desktopVisibleCount : mobileVisibleCount
  );
  
  // Watch for changes in the store's page number and update the local ref
  watch(
    () => getCurrentPage(store),
    (newVal) => {
      if (newVal !== pageNumber.value) {
        pageNumber.value = newVal;
      }
    }
  );
  
  // Watch for changes in the local page number and update the store
  watch(pageNumber, async (newVal, oldVal) => {
    if (newVal !== oldVal) {
      // Update the page in the store
      setPage(store, newVal);
      
      // If a fetch function is provided, call it to load the data for the new page
      if (fetchData) {
        await fetchData(store);
      }
    }
  });
  
  return {
    pageNumber,
    pageLength,
    totalVisible
  };
}
